import React from 'react'
import PropTypes from 'prop-types'
import { Card, Row, Col, Typography, Statistic, Button, Space, Tag, Empty } from 'antd'
import {
  BarChartOutlined,
  CalendarOutlined,
  DollarCircleOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons'
import { Bar } from 'react-chartjs-2'
import styled from 'styled-components'

const { Title, Text } = Typography

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  .ant-card-head {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-bottom: none;
    border-radius: 8px 8px 0 0;

    .ant-card-head-title {
      color: white;
      font-weight: 600;
    }
  }
`

const YearSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  justify-content: center;
`

const ChartContainer = styled.div`
  margin-top: 16px;
  min-height: 400px;
`

const StatCard = styled(Card)`
  text-align: center;
  height: 100%;
  border-left: 4px solid #1890ff;
`

const Transactions = ({
  data,
  chartOptions,
  year,
  onYearChange,
  loading,
  labels
}) => {
  if (loading) {
    return <Card loading />
  }

  const getTotalTransactions = () => {
    if (!data?.datasets) return 0
    return data.datasets.reduce((total, dataset) => {
      return total + dataset.data.reduce((sum, val) => sum + (val || 0), 0)
    }, 0)
  }

  const getCurrentMonthData = () => {
    if (!data?.datasets || !labels) return []

    const currentMonth = labels[labels.length - 1]
    return data.datasets.map(dataset => ({
      type: dataset.label,
      value: dataset.data[dataset.data.length - 1] || 0,
      color: dataset.backgroundColor
    }))
  }

  const getTopTransactionTypes = () => {
    if (!data?.datasets) return []

    return data.datasets
      .map(dataset => ({
        type: dataset.label,
        total: dataset.data.reduce((sum, val) => sum + (val || 0), 0),
        color: dataset.backgroundColor
      }))
      .sort((a, b) => b.total - a.total)
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#1890ff', marginBottom: 8 }}>
          <BarChartOutlined /> Статистика транзакций
        </Title>
        <Text type="secondary">
          Анализ транзакций по месяцам и типам платежей
        </Text>
      </div>

      {/* Year Selector */}
      <YearSelector>
        <Button
          icon={<LeftOutlined />}
          onClick={() => onYearChange(year - 1)}
          size="large"
        >
          {year - 1}
        </Button>

        <Card size="small" style={{ minWidth: 120 }}>
          <Statistic
            title=""
            value={year}
            prefix={<CalendarOutlined />}
            valueStyle={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}
          />
        </Card>

        <Button
          icon={<RightOutlined />}
          onClick={() => onYearChange(year + 1)}
          size="large"
        >
          {year + 1}
        </Button>
      </YearSelector>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <StatCard>
            <Statistic
              title={
                <div>
                  <DollarCircleOutlined style={{ color: '#1890ff' }} /> Всего транзакций
                </div>
              }
              value={getTotalTransactions()}
              suffix="за год"
              valueStyle={{ color: '#1890ff' }}
            />
          </StatCard>
        </Col>

        <Col span={8}>
          <StatCard>
            <Statistic
              title="Типов платежей"
              value={data?.datasets?.length || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </StatCard>
        </Col>

        <Col span={8}>
          <StatCard>
            <Statistic
              title="Месяцев с данными"
              value={labels?.length || 0}
              suffix={`/ 12`}
              valueStyle={{ color: '#722ed1' }}
            />
          </StatCard>
        </Col>
      </Row>

      {/* Main Chart */}
      <StyledCard title={`Статистика транзакций за ${year} год`}>
        <ChartContainer>
          {data && data.datasets && data.datasets.length > 0 ? (
            <Bar options={chartOptions} data={data} />
          ) : (
            <Empty
              description={`Нет данных за ${year} год`}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </ChartContainer>
      </StyledCard>

      {/* Analytics Cards */}
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card title="Топ типы транзакций" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {getTopTransactionTypes().map((item, index) => (
                <div
                  key={item.type}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: index < getTopTransactionTypes().length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        backgroundColor: item.color,
                        borderRadius: 2
                      }}
                    />
                    <Text>{item.type}</Text>
                  </div>
                  <Tag color={index === 0 ? 'green' : index === 1 ? 'blue' : 'orange'}>
                    {item.total}
                  </Tag>
                </div>
              ))}
            </Space>
          </Card>
        </Col>

        <Col span={12}>
          <Card title={`Данные за ${labels?.[labels.length - 1] || 'текущий месяц'}`} size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {getCurrentMonthData().map((item, index) => (
                <div
                  key={item.type}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: index < getCurrentMonthData().length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        backgroundColor: item.color,
                        borderRadius: 2
                      }}
                    />
                    <Text>{item.type}</Text>
                  </div>
                  <Tag>{item.value}</Tag>
                </div>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Help Card */}
      <Card style={{ marginTop: 16, background: '#f6ffed', borderColor: '#b7eb8f' }}>
        <Text type="secondary">
          💡 <strong>Навигация:</strong> Используйте кнопки со стрелками для переключения между годами.
          График показывает помесячную статистику всех типов транзакций.
        </Text>
      </Card>
    </div>
  )
}

Transactions.propTypes = {
  data: PropTypes.object,
  chartOptions: PropTypes.object.isRequired,
  year: PropTypes.number.isRequired,
  onYearChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  labels: PropTypes.array
}

export default Transactions